// @flow
import numeral from 'numeral'
import React from 'react'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import Snackbar from '@material-ui/core/Snackbar'
import TextField from '@material-ui/core/TextField'
import { compare } from 'er2_web_utils'
import { getLayers, getLayer } from 'catena/er2_map_userlayers/js/utils'
import * as ids from '../constants/ids'
import * as actionTypes from '../constants/action_types'
import { getActiveTool } from '../components/geo_bar_panel'
import { validationError } from '../actions/form'

type Props = {
    actions: {
        onChangeInput: Function,
        onClickInlineHelp: Function,
        onGeoBarFetchErrorRendered: Function,
        onGeoBarResultRendered: Function,
        onGeoBarSubmitReclassify: Function,
        onGeoBarSelectLayer: Function,
    },
    mapsources: object,
    theme?: object,
}

function isLayerSelected(tool) {
    return tool.targetLayer.length > 0
}

function isValid(tool) {
    return tool.targetLayer.length > 0 && !!tool.classifications
}

function fetchClassifictions(props) {
    const tool = getActiveTool(props)
    const layers = getLayers(props.mapsources)
    const targetLayer = layers.find(lyr => lyr.id === tool.targetLayer)
    props.actions.onGeoBarFetchClassifications(targetLayer, tool.classificationType, tool.classificationCount)
}

function reclassify(props) {
    const tool = getActiveTool(props)
    const layers = getLayers(props.mapsources)
    const targetLayer = layers.find(lyr => lyr.id === tool.targetLayer)
    props.actions.onGeoBarSubmitReclassify(targetLayer, tool.classifications)
}

function targetLayerFilter(lyr, props) {
    if (!lyr.layer_type || lyr.layer_type.toLowerCase() !== 'raster') {
        return false
    }
    return true
}

function isValidDistance(distance: number, dispatch, getState) {
    const castDistance = parseInt(distance, 10)
    if (castDistance === 'NaN' || castDistance < 0) {
        const error = {
            id: ids.GEO_BAR_RECLASS,
            type: actionTypes.VALIDATION_ERROR_INVALID_FIELD,
            message: 'Please provide a valid number of classifications',
        }
        dispatch(validationError(error))
        return false
    }
    return true
}

function formatClassifications(rawClassifications) {
    const result = []
    rawClassifications.forEach((cls, idx) => {
        if (idx < rawClassifications.length - 1) {
            result.push(`${numeral(cls).format('0,0.0')} - ${numeral(rawClassifications[idx + 1]).format('0,0.0')}`)
        }
    })
    return result
}

class GeoBarReclass extends React.Component<Props> {
    renderNotifications(theme) {
        const tool = getActiveTool(this.props)
        const layerName = tool.result ? tool.result.layerName : ''
        const message = layerName ? `Result saved to ${layerName}` : 'No result found for this operation'
        return (<div>
            <Snackbar
                open={tool.resultRendered === false}
                autoHideDuration={6000}
                onClose={() => {
                    this.props.actions.onGeoBarResultRendered(tool.id)
                    if (this.props.actions.onUpdateUserLayers) this.props.actions.onUpdateUserLayers()
                }}
                message={<span id="message-id">{message}</span>}
            />
            <Snackbar
                open={tool.fetchError}
                autoHideDuration={6000}
                onClose={() => this.props.actions.onGeoBarFetchErrorRendered(tool.id)}
                message={<span id="message-id">Error performing operation</span>}
            />
        </div>)
    }

    renderButtons(theme) {
        const tool = getActiveTool(this.props)
        const canFetchClassifications = isLayerSelected(tool) && tool.classificationType.length && tool.classificationCount > 0
        return (<React.Fragment>
            <div className={theme.formGroup}>
                {isLayerSelected(tool) &&
                <Button
                    classes={{
                        root: theme.buttonSubmit,
                    }}
                    disabled={!canFetchClassifications}
                    onClick={() => fetchClassifictions(this.props)}
                >
                    {!tool.isFetchingClassifications &&
                        <span>Fetch Classifications</span>
                    }
                    {tool.isFetchingClassifications &&
                        <span><i className="fa fa-refresh fa-spin" />&nbsp;Fetching...</span>
                    }
                </Button>
                }
                {tool.classifications &&
                <Button
                    classes={{
                        root: theme.buttonSubmit,
                    }}
                    disabled={!isValid(tool)}
                    onClick={() => reclassify(this.props)}
                >
                    {!tool.isFetching &&
                    <span>Submit</span>
                    }
                    {tool.isFetching &&
                    <span><i className="fa fa-refresh fa-spin" />&nbsp;Running...</span>
                    }
                </Button>
                }
            </div>
        </React.Fragment>)
    }

    renderClassificationsForm(theme) {
        const tool = getActiveTool(this.props)
        if (isLayerSelected(tool)) {
            return (<React.Fragment>
                <div className={theme.controlGroup}>
                    <FormControl
                        classes={{
                            root: theme.classificationType,
                        }}
                    >
                        <InputLabel htmlFor={ids.GEO_BAR_RECLASS_TYPE}>Classify By</InputLabel>
                        <Select
                            value={tool.classificationType}
                            inputProps={{
                                name: ids.GEO_BAR_RECLASS_TYPE,
                                id: ids.GEO_BAR_RECLASS_TYPE,
                            }}
                            onChange={evt =>
                                this.props.actions.onChangeInput(evt.target.value, ids.GEO_BAR_RECLASS_TYPE)}
                        >
                            <MenuItem key={ids.GEO_BAR_EQUAL_DISTANCE} value={ids.GEO_BAR_EQUAL_DISTANCE}>Equal Distance</MenuItem>
                            <MenuItem key={ids.GEO_BAR_NATURAL_BREAKS} value={ids.GEO_BAR_NATURAL_BREAKS}>Natural Breaks</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl
                        classes={{
                            root: theme.classificationCount,
                        }}
                    >
                        <TextField
                            id={ids.GEO_BAR_RECLASS_COUNT}
                            className={theme.textField}
                            label="Classifications"
                            value={tool.classificationCount}
                            onChange={evt => this.props.actions.onChangeInput(evt.target.value, ids.GEO_BAR_RECLASS_COUNT, isValidDistance)}
                            type="number"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            margin="normal"
                        />
                    </FormControl>
                </div>
            </React.Fragment>)
        }
        return (null)
    }

    renderClassifications(theme) {
        const tool = getActiveTool(this.props)
        if (tool.classifications) {
            const classifications = formatClassifications(tool.classifications)
            return (
                <table className={theme.table}>
                    <tbody>
                        <tr className={theme.tableHeaderRow}>
                            <th className={theme.tableHeaderCell}>Range</th>
                            <th className={theme.tableHeaderCell}>New Value</th>
                        </tr>
                        {classifications.map((cls, idx, arr) => (
                            <tr key={cls} className={theme.tableRow}>
                                <td className={theme.tableCell}>{cls}</td>
                                <td className={theme.tableCell}>{idx + 1}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )
        }
        return (null)
    }

    renderTool(theme) {
        const tool = getActiveTool(this.props)
        const layers = getLayers(this.props.mapsources)
        return (<React.Fragment>
            <div className={theme.controlGroup}>
                <FormControl
                    classes={{
                        root: theme.muiFormControl,
                    }}
                >
                    <InputLabel htmlFor={ids.GEO_BAR_TARGET_LAYER}>Target Layer</InputLabel>
                    <Select
                        value={tool.targetLayer}
                        inputProps={{
                            name: ids.GEO_BAR_TARGET_LAYER,
                            id: ids.GEO_BAR_TARGET_LAYER,
                        }}
                        onChange={evt =>
                            this.props.actions.onGeoBarSelectLayer(true, layers.find(lyr => lyr.id === evt.target.value),
                                tool, { kind: 'targetLayer' })}
                    >
                        {layers.filter(lyr => targetLayerFilter(lyr, this.props)).sort((a, b) => compare(a.name, b.name)).map(layer => (
                            <MenuItem key={layer.id} value={layer.id}>{layer.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
            {this.renderClassificationsForm(theme)}
            {this.renderClassifications(theme)}
            {this.renderButtons(theme)}
        </React.Fragment>)
    }

    render() {
        const theme = this.props.theme
        const tool = getActiveTool(this.props)
        const helpActive = tool.helpActive

        return (<div className={theme.geoBarCtr}>
            <div className={theme.formGroup}>
                <label className={theme.toolLabel}>Reclassify Tool</label>
                <i
                    className={`${theme.helpIcon}
                    ${helpActive ? theme.helpIconActive : ''}
                    fa fa-question-circle`}
                    onClick={() => this.props.actions.onClickInlineHelp(ids.GEO_BAR_RECLASS)}
                    role="button"
                    tabIndex={0}
                />
            </div>
            {helpActive &&
            <div className={theme.helpInlineCtr}>
                <div className={theme.helpInline}>
                    <p>
                        Reclassify a raster layer.
                    </p>
                    <p>
                        To reclassify a raster, first select a raster.
                    </p>
                    <p>
                        Once the raster is selected, break the raster into classifications.  To do this, select a
                        method to use to break the raster into logical segments.
                    </p>
                    <p>
                        Using Natural Breaks will divide the raster based on groupings that exist naturally in the
                        raster.  Equal distance will divide the raster into evenly-spaced groups between the smallest
                        and largest value contained in the raster.
                    </p>
                    <p>
                        Once a method is found, select the number of classifications or groups you wish the new raster
                        to be segmented.  Click Fetch Classifications to have the classifications generated for you.
                    </p>
                    <p>
                        When the classifications have been generated, you are ready to reclassify the raster.  Click on
                        Submit to perform the operation.  A new raster with the resulting reclassification will be
                        added to your user layers above.
                    </p>
                </div>
            </div>
            }
            <div className={theme.toolCtr}>
                {this.renderTool(theme)}
            </div>
            {this.renderNotifications(theme)}
        </div>)
    }
}

export default GeoBarReclass
